/**
 * The two mount points. The home slot renders the walkthrough's offer to a
 * user who has not seen it; the canvas slot runs the walkthrough on the canvas
 * it was started from. Everything else about onboarding stays inside this
 * folder.
 */

import { useSidebarContext } from "@/context/SidebarContext";
import Sentry from "@/integrations/Sentry";
import { useCanvasRoute } from "@/pages/canvas/context/CanvasRouteContext";
import {
  Component,
  useCallback,
  useEffect,
  useSyncExternalStore,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { StartNotice } from "./StartNotice";
import { TourReset } from "./TourReset";
import { Director } from "./director/Director";
import { restoreRail } from "./director/rail";
import { useOnboardingEnabled, useOnboardingStage } from "./gate";
import { useHandoff } from "./handoff";
import { useOnboardingRecord, useOnboardingStatus } from "./status";
import { openTourPatch, useTourProgress } from "./tour/progress";
import { DEFAULT_TOUR, TOURS } from "./tours";
import type { Surface, TourDefinition, TourId } from "./types";

interface SlotProps {
  surface: Surface;
  /** Another first-run surface owns this view; render nothing. */
  suppressed?: boolean;
}

export function OnboardingSlot({ surface, suppressed = false }: SlotProps) {
  return surface === "canvas" ? (
    <CanvasSlot />
  ) : (
    <HomeSlot suppressed={suppressed} />
  );
}

function HomeSlot({ suppressed }: { suppressed: boolean }) {
  const stage = useOnboardingStage();
  const show = !suppressed && stage === "new";
  const tour = useLazyTour(show ? DEFAULT_TOUR : null);
  const Home = show && tour ? tour.home : null;
  // A tab closed mid-tour left the rail closed; the home puts it back.
  const { setRailOpen } = useSidebarContext();
  useEffect(() => restoreRail(setRailOpen), [setRailOpen]);
  return (
    <>
      <TourReset />
      {(stage === "started" || stage === "skipped") && <StartNotice />}
      {Home && (
        <SlotBoundary surface="home">
          <Home />
        </SlotBoundary>
      )}
    </>
  );
}

function CanvasSlot() {
  // Latched before any chunk loads, then stripped from history.
  const handoff = useHandoff();
  const enabled = useOnboardingEnabled();
  const { canvasId } = useCanvasRoute();
  const status = useOnboardingStatus();
  const { record } = useOnboardingRecord();
  const tourId = handoff?.tour ?? DEFAULT_TOUR;
  const progress = useTourProgress(tourId);
  const claimed = progress?.canvasId === canvasId;
  // The starter points the record at the canvas before it navigates; a handoff
  // landing on a canvas the record does not name claims it here.
  useEffect(() => {
    if (!enabled || !handoff || !canvasId || !record || claimed) return;
    void status.patch(openTourPatch(record, tourId, canvasId));
  }, [enabled, handoff, canvasId, record, claimed, status, tourId]);
  // Finished, or left through "Exit tour": the canvas is an ordinary one.
  const running =
    enabled &&
    !!canvasId &&
    !!progress &&
    claimed &&
    !progress.done &&
    !progress.exited &&
    record?.global !== "complete";
  const tour = useLazyTour(running ? tourId : null);
  if (!running || !tour || !canvasId || !progress) return null;
  return (
    <SlotBoundary surface="canvas">
      <Director tour={tour} canvasId={canvasId} progress={progress} />
    </SlotBoundary>
  );
}

interface Loaded {
  value?: TourDefinition;
  error?: unknown;
  pending?: Promise<void>;
}

const modules = new Map<string, Loaded>();
const EMPTY: Loaded = {};

function load(id: TourId): Loaded {
  const existing = modules.get(id);
  if (existing) return existing;
  // Settling replaces the entry so the snapshot changes identity.
  const started: Loaded = {
    pending: TOURS[id]().then(
      (mod) => {
        modules.set(id, { value: mod.default });
      },
      (error: unknown) => {
        Sentry.captureException(error);
        modules.set(id, { error });
      },
    ),
  };
  modules.set(id, started);
  return started;
}

/** One load per tour, shared by both slots; null while nothing needs it. */
function useLazyTour(id: TourId | null): TourDefinition | undefined {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!id) return () => {};
      const entry = load(id);
      if (!entry.pending) return () => {};
      let live = true;
      void entry.pending.then(() => {
        if (live) onChange();
      });
      return () => {
        live = false;
      };
    },
    [id],
  );
  const getSnapshot = useCallback(
    () => (id ? (modules.get(id) ?? EMPTY) : EMPTY),
    [id],
  );
  return useSyncExternalStore(subscribe, getSnapshot).value;
}

interface BoundaryProps {
  surface: Surface;
  children: ReactNode;
}

/** A throw inside onboarding takes onboarding down, never the page. */
class SlotBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.withScope((scope) => {
      scope.setTag("onboarding_surface", this.props.surface);
      scope.setExtra("componentStack", info.componentStack);
      Sentry.captureException(error);
    });
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}
